import Phaser from 'phaser';
import type { BuildingId } from '@/models/types';

interface BuildingSprite {
  container: Phaser.GameObjects.Container;
  gfx: Phaser.GameObjects.Graphics;
  levelBadge: Phaser.GameObjects.Text;
  currentLevel: number;
}

// Color palettes per building type
const PALETTES: Record<string, { wall: number; roof: number; accent: number; ground: number }> = {
  // Gold buildings — warm browns/gold
  mine:     { wall: 0x8B7355, roof: 0x6B4226, accent: 0xDAA520, ground: 0x5C4033 },
  market:   { wall: 0xC4A35A, roof: 0x8B6914, accent: 0xFFD700, ground: 0x6B5B3A },
  treasury: { wall: 0xD4AF37, roof: 0xA08030, accent: 0xFFE55C, ground: 0x7B6B4A },
  // Food buildings — greens
  farm:     { wall: 0x6B8E23, roof: 0x556B2F, accent: 0x9ACD32, ground: 0x3B5323 },
  mill:     { wall: 0x8FBC8F, roof: 0x5F8A5F, accent: 0xADFF2F, ground: 0x4A6B4A },
  granary:  { wall: 0x7B8B6A, roof: 0x5B6B4A, accent: 0xBDB76B, ground: 0x4B5B3A },
  // Faith buildings — purples
  shrine:   { wall: 0x7B68AE, roof: 0x5B4897, accent: 0xBB99FF, ground: 0x3D2D5C },
  temple:   { wall: 0x9370DB, roof: 0x6A3DBF, accent: 0xD8BFD8, ground: 0x4B3070 },
  oracle:   { wall: 0x8A2BE2, roof: 0x6A0DAD, accent: 0xE6E6FA, ground: 0x4A1080 },
  // Knowledge buildings — blues
  school:   { wall: 0x5B7DB1, roof: 0x3B5998, accent: 0x87CEEB, ground: 0x2B4570 },
  library:  { wall: 0x6495ED, roof: 0x4169E1, accent: 0xB0C4DE, ground: 0x2B3D60 },
  academy:  { wall: 0x4682B4, roof: 0x2E5984, accent: 0xADD8E6, ground: 0x1E3A5F },
};

export class BuildingVisuals {
  private scene: Phaser.Scene;
  private sprites: Map<string, BuildingSprite> = new Map();
  private terrain: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, buildingIds: string[], centerX: number, baseY: number) {
    this.scene = scene;

    // Draw terrain
    this.terrain = scene.add.graphics();
    this.drawTerrain(centerX, baseY, buildingIds.length);

    const cols = 6;
    const spacingX = 68;
    const spacingY = 72;
    const startX = centerX - (cols - 1) * spacingX / 2;

    buildingIds.forEach((id, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * spacingX;
      const y = baseY + row * spacingY;
      this.createBuilding(id, x, y);
    });
  }

  private drawTerrain(cx: number, cy: number, count: number): void {
    const cols = 6;
    const rows = Math.ceil(count / cols);
    const w = cols * 68 + 40;
    const h = rows * 72 + 40;

    // Grass area
    this.terrain.fillStyle(0x1a2e1a, 0.3);
    this.terrain.fillRoundedRect(cx - w / 2, cy - 30, w, h, 12);

    // Grid paths
    this.terrain.lineStyle(1, 0x2a3a2a, 0.3);
    for (let r = 0; r < rows; r++) {
      this.terrain.lineBetween(cx - w / 2 + 10, cy + r * 72 + 36, cx + w / 2 - 10, cy + r * 72 + 36);
    }
  }

  private createBuilding(id: string, x: number, y: number): void {
    const container = this.scene.add.container(x, y);
    const gfx = this.scene.add.graphics();

    this.drawBuilding(gfx, id, 0);

    const levelBadge = this.scene.add.text(0, 22, '', {
      fontSize: '10px',
      color: '#c9a44c',
      fontFamily: 'system-ui, sans-serif',
      fontStyle: 'bold',
      backgroundColor: '#0a0a1aCC',
      padding: { x: 3, y: 1 },
    }).setOrigin(0.5);

    container.add([gfx, levelBadge]);
    this.sprites.set(id, { container, gfx, levelBadge, currentLevel: 0 });
  }

  private drawBuilding(gfx: Phaser.GameObjects.Graphics, id: string, level: number): void {
    gfx.clear();
    const p = PALETTES[id] ?? { wall: 0x888888, roof: 0x666666, accent: 0xAAAAAA, ground: 0x444444 };

    if (level === 0) {
      // Empty plot — just a dim outline
      gfx.lineStyle(1, 0x333333, 0.4);
      gfx.strokeRect(-14, -10, 28, 24);
      gfx.fillStyle(0x1a1a2a, 0.2);
      gfx.fillRect(-14, -10, 28, 24);

      // "+" hint
      gfx.lineStyle(1, 0x444444, 0.3);
      gfx.lineBetween(-4, 2, 4, 2);
      gfx.lineBetween(0, -2, 0, 6);
      return;
    }

    // Scale with level
    const scale = 1 + Math.min(0.6, level * 0.04);
    const w = Math.floor(24 * scale);
    const h = Math.floor(20 * scale);
    const roofH = Math.floor(10 * scale);

    // Ground pad
    gfx.fillStyle(p.ground, 0.5);
    gfx.fillEllipse(0, h / 2 + 2, w + 8, 8);

    // Wall
    gfx.fillStyle(p.wall, 0.85);
    gfx.fillRect(-w / 2, -h / 2 + roofH, w, h - roofH);

    // Roof — triangle
    gfx.fillStyle(p.roof, 0.9);
    gfx.fillTriangle(-w / 2 - 3, -h / 2 + roofH, w / 2 + 3, -h / 2 + roofH, 0, -h / 2 - 2);

    // Door
    const doorW = Math.max(4, Math.floor(w * 0.2));
    const doorH = Math.max(6, Math.floor(h * 0.3));
    gfx.fillStyle(p.ground, 0.8);
    gfx.fillRect(-doorW / 2, h / 2 - doorH, doorW, doorH);

    // Windows (appear at level 3+)
    if (level >= 3) {
      const winCount = Math.min(3, Math.floor(level / 3));
      const winSize = 3;
      gfx.fillStyle(p.accent, 0.6);
      for (let i = 0; i < winCount; i++) {
        const wx = -w / 3 + (i * w / 3);
        gfx.fillRect(wx - winSize / 2, -h / 2 + roofH + 4, winSize, winSize);
      }
    }

    // Chimney (level 5+)
    if (level >= 5) {
      gfx.fillStyle(p.roof, 0.7);
      gfx.fillRect(w / 4, -h / 2 - 6, 4, 8);
    }

    // Flag (level 10+)
    if (level >= 10) {
      gfx.fillStyle(p.accent, 0.8);
      gfx.fillRect(-1, -h / 2 - 12, 2, 14);
      gfx.fillTriangle(1, -h / 2 - 12, 1, -h / 2 - 6, 8, -h / 2 - 9);
    }

    // Glow ring (level 15+)
    if (level >= 15) {
      gfx.lineStyle(1, p.accent, 0.25 + Math.sin(Date.now() / 500) * 0.1);
      gfx.strokeCircle(0, 2, w / 2 + 10);
    }

    // Border highlight
    gfx.lineStyle(1, p.accent, 0.3);
    gfx.strokeRect(-w / 2, -h / 2 + roofH, w, h - roofH);
  }

  update(buildingLevels: Record<BuildingId, number>): void {
    for (const [id, sprite] of this.sprites) {
      const level = buildingLevels[id] ?? 0;

      if (level !== sprite.currentLevel) {
        const wasZero = sprite.currentLevel === 0;
        sprite.currentLevel = level;
        this.drawBuilding(sprite.gfx, id, level);
        sprite.levelBadge.setText(level > 0 ? `${level}` : '');

        // Animation on upgrade
        if (level > 0) {
          this.scene.tweens.add({
            targets: sprite.container,
            scaleX: wasZero ? { from: 0.3, to: 1 } : 1.15,
            scaleY: wasZero ? { from: 0.3, to: 1 } : 1.15,
            duration: wasZero ? 300 : 120,
            yoyo: !wasZero,
            ease: wasZero ? 'Back.easeOut' : 'Quad.easeOut',
          });

          // Particle burst on upgrade
          if (!wasZero) {
            this.spawnParticles(sprite.container.x, sprite.container.y, PALETTES[id]?.accent ?? 0xFFFFFF);
          }
        }
      }

      // Animate glow ring for high-level buildings
      if (sprite.currentLevel >= 15) {
        this.drawBuilding(sprite.gfx, id, sprite.currentLevel);
      }
    }
  }

  private spawnParticles(x: number, y: number, color: number): void {
    for (let i = 0; i < 6; i++) {
      const p = this.scene.add.circle(x, y, 2, color, 0.8);
      const angle = (i / 6) * Math.PI * 2;
      this.scene.tweens.add({
        targets: p,
        x: x + Math.cos(angle) * 20,
        y: y + Math.sin(angle) * 20,
        alpha: 0,
        scale: 0,
        duration: 400,
        ease: 'Quad.easeOut',
        onComplete: () => p.destroy(),
      });
    }
  }

  destroy(): void {
    this.terrain.destroy();
    for (const [, sprite] of this.sprites) {
      sprite.container.destroy();
    }
    this.sprites.clear();
  }
}
